
import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import { useI18n } from '../../i18n/useI18n';

interface RequireAuthProps {
  children: ReactNode;
}

/**
 * Guard: Requiere autenticación
 * Si no hay sesión, redirige a /login
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const { session, loading } = useAuth();
  const { t } = useI18n();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gray-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

interface RequireRoleProps {
  children: ReactNode;
  role: 'superadmin' | 'admin' | 'client' | 'reseller';
  /** Permitir también estos roles adicionales */
  allowAlso?: Array<'superadmin' | 'admin' | 'client' | 'reseller' | 'photographer'>;
}

/**
 * Guard: Requiere rol específico
 * Si no tiene el rol, muestra mensaje de acceso denegado
 * 
 * Roles del sistema:
 * - superadmin: Control total, acceso /sa/*
 * - admin: Negocio/estudio que paga, acceso /app/*
 * - reseller: Distribuidor (preparado para futuro)
 * - client: Cliente final (portal)
 * 
 * Nota: 'photographer' se trata como 'admin' para compatibilidad con datos antiguos
 */
export function RequireRole({ children, role, allowAlso = [] }: RequireRoleProps) {
  const { profile, loading } = useAuth();
  const { t } = useI18n();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gray-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Normalizar rol del usuario (photographer -> admin para compatibilidad)
  const userRole = profile?.role === 'photographer' ? 'admin' : profile?.role;
  
  // Verificar si el usuario tiene el rol requerido o uno de los permitidos
  const allowedRoles = [role, ...allowAlso];
  // También permitir 'photographer' si 'admin' está en la lista (compatibilidad)
  if (allowedRoles.includes('admin')) {
    allowedRoles.push('photographer' as any);
  }
  
  const hasAccess = profile && allowedRoles.includes(userRole as any);

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-lock-line text-3xl text-red-600"></i>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('guards.noAccess')}
          </h1>
          <p className="text-gray-600 mb-6">
            {t('guards.noAccessDescription')}
          </p>
          <a
            href="/app"
            className="inline-flex items-center justify-center px-6 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            {t('guards.backToApp')}
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
